import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "pinkvideo_favorites";

const readFavorites = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};

const listeners = new Set<(ids: string[]) => void>();

const notify = (ids: string[]) => {
  listeners.forEach((fn) => fn(ids));
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const handler = (ids: string[]) => setFavorites(ids);
    listeners.add(handler);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setFavorites(readFavorites());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const persist = useCallback((ids: string[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    setFavorites(ids);
    notify(ids);
  }, []);

  const toggleFavorite = useCallback(
    (id: string) => {
      const current = readFavorites();
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist]
  );

  const removeFavorite = useCallback(
    (id: string) => {
      const next = readFavorites().filter((x) => x !== id);
      persist(next);
    },
    [persist]
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, removeFavorite, isFavorite };
};